'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  borrarSesion,
  formatearDuracion,
  getSesionesDeBanda,
  guardarSesion,
} from '@/lib/actions';
import {
  SESION_SOLAPE_MAX_MS,
  SESION_VOLUMEN_DEF,
  SESION_VOLUMEN_MAX,
  SESION_VOLUMEN_MIN,
  type MarchaDoc,
  type SesionBanda,
  type SesionPista,
} from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowDown,
  ArrowUp,
  Link2,
  ListMusic,
  Loader2,
  Plus,
  Save,
  Trash2,
  Volume2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Editor de SESIONES: el guion de una procesión.
 *
 * Una sesión es una lista ordenada de audios de la banda, cada uno con su volumen
 * y con cuánto se enlaza con el anterior. En el juego se elige entera desde el
 * mando de la banda y suena sola de principio a fin.
 *
 * Dos cosas que conviene entender de los controles:
 *
 * · **El volumen no es "más fuerte", es "más lejos".** En el juego la ganancia de
 *   una fuente está topada, así que subir el volumen alarga la distancia a la que
 *   se oye la marcha. Por eso la etiqueta habla de alcance.
 * · **El enlace es cuánto se ADELANTA una pista**, no cuánto dura el fundido: con
 *   5 s, la marcha entra cuando a la anterior le quedan cinco segundos, y durante
 *   ese rato suenan las dos.
 */
export function SesionesEditor({
  bandaId,
  marchas,
}: {
  bandaId: string;
  marchas: MarchaDoc[];
}) {
  const { toast } = useToast();
  const [sesiones, setSesiones] = useState<SesionBanda[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<SesionBanda | null>(null);
  const [aBorrar, setABorrar] = useState<SesionBanda | null>(null);

  /** Solo se pueden meter audios ya aceptados: los pendientes no existen en el juego. */
  const disponibles = useMemo(
    () => marchas.filter((m) => m.estado === 'aceptada'),
    [marchas],
  );

  const recargar = useCallback(async () => {
    setCargando(true);
    setSesiones(await getSesionesDeBanda(bandaId));
    setCargando(false);
  }, [bandaId]);

  useEffect(() => { recargar(); }, [recargar]);

  const nueva = () => setEditando({
    id: '', bandaId, bandaNombre: '', nombre: '', pistas: [],
  });

  const borrar = async () => {
    if (!aBorrar) return;
    const res = await borrarSesion(aBorrar.id);
    toast({
      title: res.success ? 'Hecho' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    setABorrar(null);
    recargar();
  };

  if (editando) {
    return (
      <EditorUna
        sesion={editando}
        disponibles={disponibles}
        onCerrar={() => setEditando(null)}
        onGuardado={() => { setEditando(null); recargar(); }}
      />
    );
  }

  return (
    <section className="rounded-2xl border border-white/8 bg-card/40 p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-headline text-lg font-bold">Sesiones</h2>
          <p className="text-xs text-muted-foreground">
            El guion de una procesión: qué suena, en qué orden, a qué alcance y cómo se
            enlaza. En el juego se elige entera desde el mando de la banda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cargando && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Button size="sm" onClick={nueva} disabled={disponibles.length === 0}>
            <Plus className="mr-1.5 h-4 w-4" /> Nueva
          </Button>
        </div>
      </header>

      {disponibles.length === 0 ? (
        <p className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
          Todavía no hay audios aprobados en esta banda. Sube alguno y, cuando lo
          aprueben, podrás montar sesiones con él.
        </p>
      ) : sesiones.length === 0 ? (
        <p className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
          Ninguna sesión todavía. Crea una con «Nueva» — por ejemplo
          «Procesión Domingo de Ramos».
        </p>
      ) : (
        <ul className="space-y-1.5">
          {sesiones.map((s) => (
            <li key={s.id} className="flex items-center gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
              <ListMusic className="h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.nombre}</p>
                <p className="text-[0.7rem] text-muted-foreground">
                  {s.pistas?.length || 0} pistas
                  {(s.pistas || []).some((p) => p.solapeMs > 0) && ' · con enlaces'}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditando(s)}>Editar</Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => setABorrar(s)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar «{aBorrar?.nombre}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Desaparece del mando de la banda en el juego. Los audios no se tocan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function EditorUna({
  sesion,
  disponibles,
  onCerrar,
  onGuardado,
}: {
  sesion: SesionBanda;
  disponibles: MarchaDoc[];
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const { toast } = useToast();
  const [nombre, setNombre] = useState(sesion.nombre);
  const [pistas, setPistas] = useState<SesionPista[]>(sesion.pistas || []);
  const [guardando, setGuardando] = useState(false);

  const porId = useMemo(
    () => new Map(disponibles.map((m) => [m.id, m])),
    [disponibles],
  );

  const total = useMemo(() => {
    // Duración aproximada: la suma menos lo que se solapa.
    let ms = 0;
    pistas.forEach((p, i) => {
      ms += porId.get(p.marchaId)?.duracionMs || 0;
      if (i > 0) ms -= p.solapeMs;
    });
    return Math.max(0, ms);
  }, [pistas, porId]);

  const anadir = (m: MarchaDoc) => setPistas((ps) => [...ps, {
    marchaId: m.id,
    nombre: m.nombre,
    volumen: SESION_VOLUMEN_DEF,
    solapeMs: 0,
  }]);

  const cambiar = (i: number, campo: Partial<SesionPista>) =>
    setPistas((ps) => ps.map((p, j) => (j === i ? { ...p, ...campo } : p)));

  const mover = (i: number, d: number) => setPistas((ps) => {
    const j = i + d;
    if (j < 0 || j >= ps.length) return ps;
    const c = [...ps];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });

  const quitar = (i: number) => setPistas((ps) => ps.filter((_, j) => j !== i));

  const guardar = async () => {
    setGuardando(true);
    const res = await guardarSesion({
      id: sesion.id || undefined,
      bandaId: sesion.bandaId,
      nombre: nombre.trim(),
      pistas,
    });
    setGuardando(false);
    toast({
      title: res.success ? 'Guardada' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    if (res.success) onGuardado();
  };

  return (
    <section className="rounded-2xl border border-primary/25 bg-card/40 p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Label htmlFor="sn" className="text-xs">Nombre de la sesión</Label>
          <Input
            id="sn"
            className="mt-1.5"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Procesión Domingo de Ramos"
            maxLength={80}
          />
        </div>
        <Button variant="ghost" size="icon" className="mt-6" onClick={onCerrar}>
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {/* Guion */}
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="text-sm font-semibold">Guion</h3>
            <span className="text-[0.7rem] text-muted-foreground">
              {pistas.length} pistas · ~{formatearDuracion(total)}
            </span>
          </div>

          {pistas.length === 0 ? (
            <p className="rounded-xl bg-muted/40 p-4 text-xs text-muted-foreground">
              Vacío. Añade audios desde la derecha; luego los puedes reordenar.
            </p>
          ) : (
            <ul className="space-y-2">
              {pistas.map((p, i) => {
                const m = porId.get(p.marchaId);
                return (
                  <li key={`${p.marchaId}-${i}`} className="rounded-xl bg-muted/40 p-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-center text-xs text-muted-foreground">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{m?.nombre || p.nombre}</p>
                        <p className="text-[0.65rem] text-muted-foreground">
                          {m ? formatearDuracion(m.duracionMs) : 'audio no disponible'}
                          {m && m.categoria !== 'marcha' && ` · ${m.categoria}`}
                        </p>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => mover(i, -1)} disabled={i === 0}>
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => mover(i, 1)} disabled={i === pistas.length - 1}>
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => quitar(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <label className="block">
                        <span className="flex items-center gap-1.5 text-[0.65rem] text-muted-foreground">
                          <Volume2 className="h-3 w-3" /> Alcance · {p.volumen}%
                        </span>
                        <input
                          type="range"
                          min={SESION_VOLUMEN_MIN}
                          max={SESION_VOLUMEN_MAX}
                          step={10}
                          value={p.volumen}
                          onChange={(e) => cambiar(i, { volumen: Number(e.target.value) })}
                          className="mt-1 w-full accent-primary"
                        />
                      </label>

                      {i > 0 && (
                        <label className="block">
                          <span className="flex items-center gap-1.5 text-[0.65rem] text-muted-foreground">
                            <Link2 className="h-3 w-3" />
                            {p.solapeMs > 0
                              ? `Entra ${(p.solapeMs / 1000).toFixed(0)} s antes de que acabe la anterior`
                              : 'Sin enlace (empieza al acabar la anterior)'}
                          </span>
                          <input
                            type="range"
                            min={0}
                            max={SESION_SOLAPE_MAX_MS}
                            step={1000}
                            value={p.solapeMs}
                            onChange={(e) => cambiar(i, { solapeMs: Number(e.target.value) })}
                            className="mt-1 w-full accent-primary"
                          />
                        </label>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Audios disponibles */}
        <div>
          <h3 className="mb-2 text-sm font-semibold">Añadir</h3>
          <div className="max-h-[26rem] space-y-1 overflow-y-auto pr-1">
            {disponibles.map((m) => (
              <button
                key={m.id}
                onClick={() => anadir(m)}
                className="flex w-full items-center gap-2 rounded-lg bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/60"
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{m.nombre}</span>
                  <span className="block text-[0.65rem] text-muted-foreground">
                    {formatearDuracion(m.duracionMs)}
                    {m.categoria !== 'marcha' && ` · ${m.categoria}`}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 border-t border-white/8 pt-4">
        <Button onClick={guardar} disabled={guardando || !nombre.trim()}>
          {guardando
            ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando…</>)
            : (<><Save className="mr-2 h-4 w-4" /> Guardar sesión</>)}
        </Button>
        <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
        <p className={cn('ml-auto text-[0.7rem] text-muted-foreground')}>
          Los cambios llegan al juego en unos segundos.
        </p>
      </div>
    </section>
  );
}
